import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { AUTH_TOKEN_KEY } from "@/lib/constants";
import { toast } from "sonner";
import { Mic, MicOff, Volume2, ArrowRight, Award, Play, Loader2, Square } from "lucide-react";

const LEVELS = [
  { key: "entry", label: "Entry" },
  { key: "mid", label: "Mid" },
  { key: "senior", label: "Senior" },
  { key: "executive", label: "Executive" },
];

export default function Interview() {
  const [phase, setPhase] = useState("setup"); // setup | active | complete
  const [session, setSession] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]); // per-question feedback
  const [summary, setSummary] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [role, setRole] = useState("Product Manager");
  const [level, setLevel] = useState("mid");
  const [industry, setIndustry] = useState("Tech");

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/interview/start", { role, level, industry });
      setSession(data); setPhase("active"); setCurrent(0); setFeedbackList([]); setSummary(null);
      speak(data.questions[0]?.question);
    } catch (e) { toast.error(e.response?.data?.detail || "Start failed"); } finally { setLoading(false); }
  };

  const speak = async (text) => {
    if (!text) return;
    // Try premium OpenAI TTS first; fall back to browser SpeechSynthesis
    try {
      const res = await api.post("/interview/tts", { text, voice: "onyx" }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.play().catch(() => {});
      return;
    } catch (_) {
      /* fall through to browser TTS */
    }
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0; u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch {}
  };

  const submitAnswer = async () => {
    if (!session || !answer.trim()) return;
    const q = session.questions[current];
    setLoading(true);
    try {
      const { data } = await api.post("/interview/answer", { session_id: session.id, question_id: q.id, answer });
      setFeedbackList(prev => [...prev, data]);
      setAnswer("");
      if (current + 1 >= session.questions.length) {
        const sr = await api.post(`/interview/${session.id}/finish`);
        setSummary(sr.data); setPhase("complete");
      } else {
        setCurrent(c => c + 1);
        setTimeout(() => speak(session.questions[current + 1]?.question), 400);
      }
    } catch (e) { toast.error("Submit failed"); } finally { setLoading(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4" });
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "answer.webm");
          const token = localStorage.getItem(AUTH_TOKEN_KEY);
          const res = await fetch(`${API}/interview/transcribe`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
          const j = await res.json();
          if (res.ok) setAnswer(prev => prev ? `${prev} ${j.text}` : j.text);
          else toast.error(j.detail || "Transcription failed");
        } catch { toast.error("Transcription error"); } finally { setTranscribing(false); }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Microphone permission denied"); }
  };
  const stopRecording = () => { if (mediaRecorderRef.current && recording) { mediaRecorderRef.current.stop(); setRecording(false); } };

  return (
    <div className="p-10 max-w-4xl" data-testid="interview-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 10</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Interview Simulator</h1>
          <p className="text-muted-foreground mt-2 text-sm">Voice-based mock interviews. Claude plays the interviewer. Whisper transcribes. You practice.</p>
        </div>
        <a
          href="/app/interview/live"
          data-testid="live-interview-link"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-fuchsia-500 text-primary-foreground text-xs font-mono uppercase tracking-widest px-4 py-2 hover:opacity-90 transition-opacity"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
          Try Live Voice Mode
        </a>
      </div>

      {phase === "setup" && (
        <div className="card-flat p-8 space-y-6" data-testid="interview-setup">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Target role</label>
              <input data-testid="iv-role" value={role} onChange={e=>setRole(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Industry</label>
              <input data-testid="iv-industry" value={industry} onChange={e=>setIndustry(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Level</label>
              <div className="flex gap-2 mt-2">
                {LEVELS.map(l => (
                  <button key={l.key} data-testid={`iv-level-${l.key}`} onClick={()=>setLevel(l.key)} className={`px-4 py-2 text-sm font-medium border ${level===l.key?"bg-primary border-primary text-foreground":"bg-transparent border-border text-muted-foreground hover:border-zinc-500"}`}>{l.label}</button>
                ))}
              </div>
            </div>
          </div>
          <button data-testid="iv-start" onClick={start} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3 flex items-center gap-2">{loading? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>} Start interview</button>
        </div>
      )}

      {phase === "active" && session && (
        <div className="space-y-6" data-testid="interview-active">
          <div className="flex items-center gap-3">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Question {current + 1} / {session.questions.length}</div>
            <div className="flex-1 h-1 bg-secondary"><div className="h-full bg-primary" style={{ width: `${((current)/session.questions.length)*100}%` }}/></div>
          </div>
          <div className="card-flat p-6" data-testid="iv-question-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-primary mb-2">{session.questions[current].type}</div>
                <div className="font-display font-bold text-2xl tracking-tight leading-snug">{session.questions[current].question}</div>
              </div>
              <button data-testid="iv-speak" onClick={()=>speak(session.questions[current].question)} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors p-3"><Volume2 size={16}/></button>
            </div>
          </div>

          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your answer</div>
              {!recording ? (
                <button data-testid="iv-record-start" onClick={startRecording} disabled={transcribing} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-3 py-1.5 text-xs flex items-center gap-2"><Mic size={12}/> {transcribing? "Transcribing...":"Record"}</button>
              ) : (
                <button data-testid="iv-record-stop" onClick={stopRecording} className="px-3 py-1.5 text-xs flex items-center gap-2 bg-red-500 text-foreground"><Square size={10}/> Stop</button>
              )}
            </div>
            <textarea data-testid="iv-answer" value={answer} onChange={e=>setAnswer(e.target.value)} rows={5} placeholder={recording? "Recording... speak clearly.":"Type or record your answer..."} className="w-full bg-card border border-border px-4 py-3"/>
            <button data-testid="iv-submit" onClick={submitAnswer} disabled={loading || !answer.trim()} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors mt-3 px-6 py-2.5 flex items-center gap-2 disabled:opacity-50">
              {loading? <Loader2 size={14} className="animate-spin"/> : <ArrowRight size={14}/>} {current + 1 >= session.questions.length ? "Finish" : "Next question"}
            </button>
          </div>

          {feedbackList.length > 0 && (
            <div className="space-y-3" data-testid="iv-feedback-list">
              <div className="font-display font-bold text-lg tracking-tight">Feedback so far</div>
              {feedbackList.map((f, i) => (
                <div key={i} className="card-flat p-4" data-testid={`iv-feedback-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-muted-foreground truncate">Q{i+1}: {f.question}</div>
                    <div className="font-mono font-bold text-sm">{f.feedback.score}/100</div>
                  </div>
                  <div className="text-xs text-emerald-400 mb-1">✓ {f.feedback.strengths?.join(" · ")}</div>
                  <div className="text-xs text-amber-400">△ {f.feedback.weaknesses?.join(" · ")}</div>
                  {f.feedback.quick_tip && <div className="text-xs text-foreground/80 mt-2 italic">Tip: {f.feedback.quick_tip}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {phase === "complete" && summary && (
        <div className="space-y-6" data-testid="interview-complete">
          <div className="card-flat p-8 grid md:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="w-24 h-24 bg-primary flex flex-col items-center justify-center">
              <div className="font-display font-black text-4xl leading-none">{summary.overall_score}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-1">/100</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Verdict</div>
              <div className="font-display font-bold text-2xl tracking-tight">{summary.verdict}</div>
              <div className="text-sm text-muted-foreground mt-2">{summary.closing_advice}</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card-flat p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">Top strength</div>
              <div className="text-sm">{summary.top_strength}</div>
            </div>
            <div className="card-flat p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">Top gap</div>
              <div className="text-sm">{summary.top_gap}</div>
            </div>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Action plan</div>
            <ul className="space-y-2">{(summary.action_plan||[]).map((a,i) => <li key={i} className="text-sm text-foreground/80 flex gap-2"><Award size={12} className="text-primary mt-1 shrink-0"/> {a}</li>)}</ul>
          </div>
          <button data-testid="iv-restart" onClick={()=>{ setPhase("setup"); setSession(null); }} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-3">Run another interview</button>
        </div>
      )}
    </div>
  );
}
